import { type ReactNode, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  buildAllItems,
  MASTERY_PRESETS,
  buildSkillState,
  type ExerciseSkillId,
} from "@music-app/learning-system";
import { useCardsStore, usePrefsStore } from "@music-app/persistence";

export type ExerciseMode = "listen" | "practice" | "review" | "challenge";

export const MODE_COLOR: Record<ExerciseMode, string> = {
  listen:    "#4A7FA5",
  practice:  "#6A6460",
  review:    "#3D7A5F",
  challenge: "#9E4D5A",
};

interface ModeSwitcherBarProps {
  current: ExerciseMode;
  /** When set, inactive pills become clickable and navigate to this exercise path. */
  exercisePath?: string;
}

const LABEL: Record<ExerciseMode, string> = {
  listen:    "Listen",
  practice:  "Practice",
  review:    "Review",
  challenge: "Challenge",
};

const ALL_MODES: ExerciseMode[] = ["listen", "practice", "review", "challenge"];

function useChallengeLocked(exercisePath: string | undefined): boolean {
  const cards = useCardsStore((s) => s.cards);
  const masteryPreset = usePrefsStore((s) => s.masteryPreset);
  const unlockEverything = usePrefsStore((s) => s.unlockEverything);
  const items = useMemo(() => buildAllItems(), []);

  return useMemo(() => {
    if (!exercisePath) return false;
    if (exercisePath.startsWith("/guitar/")) return true;
    if (unlockEverything) return false;
    const skillId = exercisePath.replace("/exercise/", "") as ExerciseSkillId;
    const thresholds = MASTERY_PRESETS[masteryPreset];
    return buildSkillState(skillId, items, cards, thresholds).challenge.state === "locked";
  }, [exercisePath, masteryPreset, unlockEverything, items, cards]);
}

export function ModeSwitcherBar({ current, exercisePath }: ModeSwitcherBarProps): ReactNode {
  const navigate = useNavigate();
  const challengeLocked = useChallengeLocked(exercisePath);

  return (
    <div
      className="inline-flex border border-ink/15 mb-5"
      role="group"
      aria-label="Practice mode"
    >
      {ALL_MODES.map((mode) => {
        const isActive = current === mode;
        const color = MODE_COLOR[mode];
        const baseClass = "px-4 py-1.5 font-mono text-[10px] uppercase tracking-widest border-r border-ink/15 last:border-r-0 transition-colors";

        if (isActive) {
          return (
            <span
              key={mode}
              aria-current="true"
              className={`${baseClass} select-none cursor-default`}
              style={{ background: color, color: "#F5F0E8" }}
            >
              {LABEL[mode]}
            </span>
          );
        }

        if (exercisePath) {
          if (mode === "challenge" && challengeLocked) {
            return (
              <span
                key={mode}
                title="Unlock: master 4 items first"
                className={`${baseClass} select-none cursor-not-allowed`}
                style={{ color, opacity: 0.35 }}
                aria-disabled="true"
              >
                {LABEL[mode]}
              </span>
            );
          }

          return (
            <button
              key={mode}
              type="button"
              onClick={() => void navigate(`${exercisePath}?mode=${mode}`, { replace: true })}
              className={`${baseClass} hover:bg-ink/5 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ink/30`}
              style={{ color }}
            >
              {LABEL[mode]}
            </button>
          );
        }

        return (
          <span
            key={mode}
            className={`${baseClass} select-none cursor-default`}
            style={{ color, opacity: 0.45 }}
          >
            {LABEL[mode]}
          </span>
        );
      })}
    </div>
  );
}
